'use client';

import { useRef, useEffect } from 'react';
import { uiFb } from '@/lib/i18n';

interface CommandPaletteInputProps {
  query: string;
  onQueryChange: (q: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  isFocused: boolean;
  onFocus: () => void;
  resultCount: number;
  activeDescendant?: string;
  onClose: () => void;
  lang: string;
}

export function CommandPaletteInput({
  query,
  onQueryChange,
  onKeyDown,
  isFocused,
  onFocus,
  resultCount,
  activeDescendant,
  onClose,
  lang,
}: CommandPaletteInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isFocused) {
      inputRef.current?.focus();
    }
  }, [isFocused]);

  // Auto-focus on mount
  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  return (
    <div className="flex items-center gap-3 border-b border-border-subtle px-4 py-3">
      <span id="search-instructions" className="sr-only">
        {uiFb('ui.search.instructions', 'Type to search. Use arrow keys to navigate results, Enter to open, Tab to switch sections.', lang)}
      </span>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-text-dim"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={true}
        aria-controls="command-palette-results-container"
        aria-activedescendant={activeDescendant}
        aria-autocomplete="list"
        aria-describedby="search-instructions"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        placeholder={uiFb('ui.search.placeholder', 'Search pages, events, countries...', lang)}
        className="min-w-0 flex-1 bg-transparent text-base text-text-primary placeholder:text-text-dim outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
      />
      {query && (
        <button
          onClick={() => onQueryChange('')}
          className="shrink-0 rounded-md p-1 min-h-[44px] min-w-[44px] flex items-center justify-center text-text-dim hover:text-text-secondary"
          aria-label={uiFb('ui.search.clear_search', 'Clear search', lang)}
        >
          <svg width="14" height="14" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="4" y1="4" x2="14" y2="14" /><line x1="14" y1="4" x2="4" y2="14" />
          </svg>
        </button>
      )}
      {/* Mobile close button (hidden on desktop where ESC works) */}
      <button
        onClick={onClose}
        className="shrink-0 rounded-md px-2 py-1 text-sm text-text-dim hover:text-text-secondary sm:hidden"
        aria-label={uiFb('ui.search.cancel', 'Cancel', lang)}
      >
        {uiFb('ui.search.cancel', 'Cancel', lang)}
      </button>
      <kbd className="hidden shrink-0 rounded-md border border-border-subtle bg-surface-secondary px-1.5 py-0.5 text-caption font-medium text-text-dim sm:inline-block">
        ESC
      </kbd>
    </div>
  );
}
